import {
  CONTROL_VARIETY_YES,
  DEFAULT_BLACKLISTED_TRAITS,
  DEFAULT_MAP_LAYERS,
  DEFAULT_TRAIT_STYLE,
  MAP_DISPLAY_MODES,
  OTHER_NUMERICAL_PROPERTIES_GROUP,
  RESERVED_PROPERTY_KEYS,
  isResultsTab,
} from "../constants";

import { boolStringToInt } from "../services/utils";
import chroma from "chroma-js";
import { createSelector } from "reselect";
import { getMinZoomLevel } from "../WorldMap/utils";
import otherPropertiesIcon from "../static/img/other_properties.png";

// Features with auc data incorporated as traits
export const selectFeaturesWithAUCData = createSelector(
  (state) => state.resultMap.features,
  (state) => state.resultMap.aucData,
  (features, aucData) => {
    if (!aucData.traits.length) return features;
    return features.map((feature) => ({
      ...feature,
      properties: {
        ...feature.properties,
        ...aucData.featureData[feature.properties.plot_id],
      },
    }));
  }
);

// Dates where one plot at least carries a trait. null while featuresByDate is
// not loaded, so nothing is greyed before the answer is known.
export const selectDatesWithResults = createSelector(
  (state) => state.resultMap.featuresByDate,
  (featuresByDate) =>
    featuresByDate.length === 0
      ? null
      : new Set(
          featuresByDate
            .filter(({ trait_list }) => trait_list.length > 0)
            .map(({ date }) => date)
        )
);

// Adds AUC data to each corresponding feature in featuresByDate
export const selectFeaturesByDateWithAUCData = createSelector(
  (state) => state.resultMap.featuresByDate,
  (state) => state.resultMap.aucData,
  (features, aucData) => {
    if (!aucData.traits.length) return features;
    return features.map((feature) => ({
      ...feature,
      properties: {
        ...feature.properties,
        ...aucData.featureData[feature.properties.plot_id],
      },
    }));
  }
);

// Function used by selectors to systematically apply filters to features
const filterFeatures = (
  features,
  selectedLayer,
  selectedExperiments,
  distinctExperiments,
  distinctLayers,
  selectedModalities,
  distinctModalities,
  filteringProfile,
  selectedAttributes
) => {
  // Use user-defined filtering profile if it is defined
  if (filteringProfile.scope) {
    selectedExperiments = filteringProfile.scope.experiments;
    selectedModalities = filteringProfile.scope.modalities;
    selectedLayer = filteringProfile.scope.layer;
    selectedAttributes = filteringProfile.scope.attributes;
  }

  // Do not filter on layer if less than 2 layers
  const shouldSkipLayerFilter = distinctLayers.length <= 1;

  // Do not filter on experiments if no experiments, no experiments selected, or all experiments selected
  const shouldSkipExperimentFilter =
    !distinctExperiments.length ||
    (!!selectedExperiments.length &&
      distinctExperiments.length === selectedExperiments.length);

  // Do not filter on modalities if no modalities, no modalities selected, or all modalities selected
  const shouldSkipModalityFilter =
    !distinctModalities.length ||
    (!!selectedModalities.length &&
      distinctModalities.length === selectedModalities.length);

  return features.filter(
    ({ properties: { experiment, layer, modality, ...attributesProps } }) => {
      // Check if layer filter should be applied
      const isSelectedLayer = shouldSkipLayerFilter || selectedLayer === layer;
      if (!isSelectedLayer) return false;

      // Check if experiment filter should be applied
      const isSelectedExperiment =
        shouldSkipExperimentFilter ||
        selectedExperiments.includes(experiment ?? null);

      // Check if modality filter should be applied
      const isSelectedModality =
        shouldSkipModalityFilter ||
        selectedModalities.includes(modality ?? null);

      // Check if attributes match selected attributes
      const shouldExcludeFeature = Object.entries(selectedAttributes).some(
        ([key, values]) => {
          const attributeValue = attributesProps[key] ?? null;

          // Exclude features where the attribute value is not included in the selected values
          return !values.includes(attributeValue);
        }
      );

      return (
        isSelectedExperiment && isSelectedModality && !shouldExcludeFeature
      );
    }
  );
};

// Features by date on which filterFeatures is applied
export const selectFilteredFeaturesByDate = createSelector(
  selectFeaturesByDateWithAUCData,
  (state) => state.resultMap.filters.layer,
  (state) => state.resultMap.filters.experiments,
  (state) => state.resultMap.distinctExperiments,
  (state) => state.resultMap.distinctLayers,
  (state) => state.resultMap.filters.modalities,
  (state) => state.resultMap.distinctModalities,
  (state) => state.resultMap.filteringProfile,
  (state) => state.resultMap.filters.attributes,
  filterFeatures
);

// Features  on which filterFeatures is applied
export const selectFeaturesFilteredOnExperimentAndLayer = createSelector(
  selectFeaturesWithAUCData,
  // Exact properties of filters have to be extracted
  // otherwise min or min filter update would also trigger this selector update
  (state) => state.resultMap.filters.layer,
  (state) => state.resultMap.filters.experiments,
  (state) => state.resultMap.distinctExperiments,
  (state) => state.resultMap.distinctLayers,
  (state) => state.resultMap.filters.modalities,
  (state) => state.resultMap.distinctModalities,
  (state) => state.resultMap.filteringProfile,
  (state) => state.resultMap.filters.attributes,
  filterFeatures
);

// Get unique excluded groups across all dates
export const selectFilteredOutGroupsSet = createSelector(
  (state) => state.resultMap.filteringProfile,
  (filteringProfile) => {
    if (!filteringProfile.scope) return new Set();
    return new Set(
      filteringProfile.filters.flatMap((filter) => filter.excludedGroups) // Concat all excluded groups
    );
  }
);

export const selectExcludedGroupsSet = createSelector(
  (state) => state.resultMap.filteringProfile,
  selectFilteredOutGroupsSet,
  (filteringProfile, filteredOutGroupsSet) => {
    if (!filteringProfile.scope) return new Set();
    return new Set(
      Array.from(filteredOutGroupsSet)
        .concat(filteringProfile.blacklist) // Add all blacklisted groups
        .filter((group) => !filteringProfile.whitelist.includes(group)) // Remove whitelisted groups
    );
  }
);

// Selector to select features by date, filtering excluded groups
export const selectFilteredFeaturesByDateAndExcludedGroups = createSelector(
  selectFilteredFeaturesByDate,
  selectExcludedGroupsSet,
  (features, excludedGroupsSet) =>
    excludedGroupsSet.size === 0
      ? features
      : features.filter((feature) => !excludedGroupsSet.has(feature.group))
);

// Based on current trial, returns associated contractInfo object
export const selectSelectedTrialContractInfo = createSelector(
  (state) => state.user.contracts,
  (state) => state.resultMap.trial?.contract_id,
  (contracts, contractId) =>
    contracts.find((contract) => contract.id === contractId)
);

// Returns two objects: the array of group objects containing aggregated features
// and a second object for each trait {min, max, sum, count, mean}
export const selectNumericalPropertiesMetricsAndAggregatedFeatures =
  createSelector(selectFeaturesFilteredOnExperimentAndLayer, (features) => {
    const aggregatedFeatures = {};
    const numericalPropertiesMetrics = {};

    features.forEach((feature) => {
      const { group, properties, modality, genotype } = feature;

      if (!aggregatedFeatures[group])
        aggregatedFeatures[group] = {
          features: [],
          properties: {},
          modality,
          genotype,
          isControl:
            properties.control_variety?.toLowerCase() === CONTROL_VARIETY_YES,
        };

      const groupData = aggregatedFeatures[group];
      groupData.features.push(feature);

      Object.entries(properties).forEach(([key, value]) => {
        if (
          DEFAULT_BLACKLISTED_TRAITS.includes(key.toLowerCase()) ||
          RESERVED_PROPERTY_KEYS.includes(key.toLowerCase())
        )
          return;

        if (typeof value === "number") {
          if (!numericalPropertiesMetrics[key])
            numericalPropertiesMetrics[key] = {
              min: Infinity,
              max: -Infinity,
              sum: 0,
              count: 0,
            };

          numericalPropertiesMetrics[key].min = Math.min(
            numericalPropertiesMetrics[key].min,
            value
          );
          numericalPropertiesMetrics[key].max = Math.max(
            numericalPropertiesMetrics[key].max,
            value
          );
          numericalPropertiesMetrics[key].sum += value;
          numericalPropertiesMetrics[key].count++;

          if (!groupData.properties[key]) {
            groupData.properties[key] = {
              min: value,
              max: value,
              sum: value,
              count: 1, // Count of non null values
            };
          } else {
            groupData.properties[key].min = Math.min(
              groupData.properties[key].min,
              value
            );
            groupData.properties[key].max = Math.max(
              groupData.properties[key].max,
              value
            );
            groupData.properties[key].sum += value;
            groupData.properties[key].count++;
          }
        }
      });
    });

    const aggregatedFeaturesArray = Object.entries(aggregatedFeatures).map(
      ([group, data]) => {
        Object.values(data.properties).forEach((value) => {
          value.mean = value.sum / value.count;
        });
        return {
          group,
          ...data,
        };
      }
    );

    Object.values(numericalPropertiesMetrics).forEach((value) => {
      value.mean = value.sum / value.count;
    });

    return [aggregatedFeaturesArray, numericalPropertiesMetrics];
  });

// Visualized entries deduplicated by their resolved variety group: a group and
// one of its plots (or two plots of the same group) both resolve to the same
// group and must not open two identical comparison cards. Keeps the first
// entry per group so an individually-visualized plot still opens on that plot.
export const selectDedupedVisualizedVarieties = createSelector(
  (state) => state.resultMap.visualizedVarieties,
  selectNumericalPropertiesMetricsAndAggregatedFeatures,
  (visualizedVarieties, [aggregatedFeatures]) => {
    const groupOf = (element) =>
      aggregatedFeatures.find(({ features }) =>
        features.some(({ displayId }) => displayId === element)
      )?.group ?? element;

    const seenGroups = new Set();
    return visualizedVarieties.filter((element) => {
      const group = groupOf(element);
      if (seenGroups.has(group)) return false;
      seenGroups.add(group);
      return true;
    });
  }
);

// All traits including auc traits
export const selectMergedTraits = createSelector(
  (state) => state.resultMap.traitsListForMap,
  (state) => state.resultMap.aucData.traits,
  selectNumericalPropertiesMetricsAndAggregatedFeatures,
  (traitsListForMap, aucDataTraits, [, numericalPropertiesMetrics]) => {
    const defaultTraitGroup = {
      uuid: OTHER_NUMERICAL_PROPERTIES_GROUP,
      name: OTHER_NUMERICAL_PROPERTIES_GROUP,
      icon: otherPropertiesIcon,
    };
    const traits = [...traitsListForMap, ...aucDataTraits];
    const otherTraits = Object.keys(numericalPropertiesMetrics)
      .filter((key) => !traits.some((trait) => trait.technical_name === key))
      .map((key) => ({
        name: key,
        technical_name: key,
        technicalName: key,
        traitGroup: defaultTraitGroup,
        style: DEFAULT_TRAIT_STYLE,
      }));
    return traits.concat(otherTraits);
  }
);

// Aggregated features filtered on filtering profile groups
export const selectFilteredAggregatedFeatures = createSelector(
  selectNumericalPropertiesMetricsAndAggregatedFeatures,
  selectExcludedGroupsSet,
  ([aggregatedFeatures], excludedGroups) =>
    !excludedGroups.size
      ? aggregatedFeatures
      : aggregatedFeatures.filter(({ group }) => !excludedGroups.has(group))
);

// Filtered features from filtered aggregated features
export const selectFilteredFeatures = createSelector(
  selectFilteredAggregatedFeatures,
  (aggregatedFeatures) => {
    return aggregatedFeatures.flatMap(({ features }) => features);
  }
);

// Zoom from which the plot labels show, read by the map (labels, plot click)
// and the plot search: computed once per set of plots.
export const selectMinZoomToShowLabels = createSelector(
  selectFilteredFeatures,
  getMinZoomLevel
);

// Plot labels on the map: Plot Map and Results, a property picked, their eye
// open. Read by the map to keep the plots layer mounted for them.
export const selectShowPlotLabels = ({ resultMap }) =>
  (resultMap.mapDisplayMode === MAP_DISPLAY_MODES.PLOT_MAP ||
    isResultsTab(resultMap.mapDisplayMode)) &&
  resultMap.plotLabelsVisible &&
  !!resultMap.plotLabelProperty;

// Color scales
export const selectScoutingColorScale = createSelector(
  (state) => state.resultMap.scoutingFeatures,
  (state) => state.resultMap.selectedScoutingTrait,
  (state) => state.resultMap.scoutingColorScaleStep,
  (scoutingFeatures, selectedScoutingTrait, scoutingColorScaleStep) => {
    const defaultColorScale = {
      minValue: null,
      maxValue: null,
      step: null,
      func: () => "rgba(255, 255, 255, 0)",
    };

    const style = selectedScoutingTrait?.style;
    const key = selectedScoutingTrait?.key;
    if (scoutingFeatures.length && style) {
      let minValue = null;
      let maxValue = null;
      if (style.defaultInterval) {
        minValue = style.minimumOfInterval;
        maxValue = style.maximumOfInterval;
      } else {
        scoutingFeatures.forEach((scountingFeat) => {
          if (Object.keys(scountingFeat.meta).length > 0) {
            let value = scountingFeat.meta[key].value;
            if (value !== null) {
              if (minValue === null || value < minValue) minValue = value;
              if (maxValue === null || value > maxValue) maxValue = value;
            }
            minValue = boolStringToInt(minValue);
            maxValue = boolStringToInt(maxValue);
          }
          if ((minValue !== 0 && !minValue) || (maxValue !== 0 && !maxValue))
            return defaultColorScale;
        });
      }
      let stepVal = scoutingColorScaleStep || style.step;

      return {
        minValue: minValue,
        maxValue: maxValue,
        step: stepVal,
        func: chroma
          .scale(style.color)
          .domain([minValue, maxValue])
          .mode("lrgb")
          .classes(stepVal),
      };
    }
    return defaultColorScale;
  }
);

export const selectColorScale = createSelector(
  selectFilteredFeatures,
  (state) => state.resultMap.selectedTrait,
  (state) => state.resultMap.colorScaleStep,
  (filteredFeatures, { style, technical_name }, colorScaleStep) => {
    const defaultColorScale = {
      minValue: null,
      maxValue: null,
      step: null,
      func: () => "rgba(255, 255, 255, 0)",
    };
    // If `selectedTrait` not empty then `style` is defined
    if (filteredFeatures.length && style && style.opacity !== false) {
      let minValue = null;
      let maxValue = null;

      // Expect both min and max interval to be defined if `intervalDefault` is true
      if (style.intervalDefault) {
        minValue = style.intervalMin;
        maxValue = style.intervalMax;
      } else {
        // `forEach` prefered to `Math.min`/`Math.max` or `reduce` to prevent performance or stack size problems on very large arrays
        filteredFeatures.forEach((feature) => {
          const value = feature.properties[technical_name];
          if (value != null) {
            if (minValue === null || value < minValue) minValue = value;
            if (maxValue === null || value > maxValue) maxValue = value;
          }
        });

        minValue = boolStringToInt(minValue);
        maxValue = boolStringToInt(maxValue);
      }

      if ((minValue !== 0 && !minValue) || (maxValue !== 0 && !maxValue))
        return defaultColorScale;

      const stepValue = colorScaleStep || style.step;

      return {
        minValue: minValue,
        maxValue: maxValue,
        step: stepValue,
        func: chroma
          .scale(style.color)
          .domain([minValue, maxValue])
          .mode("lrgb")
          .classes(stepValue),
      };
    }
    return defaultColorScale;
  }
);

// Varieties marked as control in data
export const selectControlVarieties = createSelector(
  selectNumericalPropertiesMetricsAndAggregatedFeatures,
  ([aggregatedFeatures]) =>
    aggregatedFeatures
      .filter(({ isControl }) => isControl)
      .map(({ group }) => group)
);

// Selected orthoimage based on selected ortholayer date and name
export const selectOrtho = createSelector(
  (state) => state.resultMap.mapLayers,
  (state) => state.resultMap.trial,
  (state) => state.resultMap.trial_date,
  (mapLayers, trial, trialDate) => {
    const orthoLayer = mapLayers.find(
      ({ id }) => id === DEFAULT_MAP_LAYERS.ORTHO_LAYER
    );
    return orthoLayer && trial.ortho_layer[trialDate]?.[orthoLayer.name];
  }
);

// Company colour palette of the contract owning the displayed trial. Sites
// inherit it, their own preferences only hold local exceptions.
export const selectContractFieldBookColors = createSelector(
  (state) => state.user.contracts,
  (state) => state.resultMap.trial?.contract_id,
  (contracts, contractId) =>
    contracts.find(({ id }) => id === contractId)?.preferences?.fieldBookColors
);

// Sometimes we need to consider experiments and modalities as an attribute ex. Attributes Profiler
export const selectAttributesIncludingExperimentsAndModalities = createSelector(
  (state) => state.resultMap.attributes,
  (state) => state.resultMap.distinctExperiments,
  (state) => state.resultMap.distinctModalities,
  (attributes, distinctExperiments, distinctModalities) => {
    const { plot, ...displayableAttributes } = attributes;
    return {
      experiment: distinctExperiments,
      modality: distinctModalities,
      ...displayableAttributes,
    };
  }
);

// The palette the editor shows: the pending edits, else the saved one.
export const selectEditedFieldBookColors = createSelector(
  (state) => state.resultMap.pendingFieldBookColors,
  (state) => state.resultMap.trial?.preferences?.fieldBookColors,
  (pending, saved) => pending ?? saved ?? {}
);

export const selectHasResults = (state) =>
  state.resultMap.traitsListForMap.length > 0;
